import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isEqual, isEmpty, head } from 'lodash';
import {
  updateSweepAlerts,
  getSweepsList,
  getSweepsAlerts,
  showThresholdError,
  showTargetError,
  closeErrorPopup,
  deleteSweepAlerts,
  setSelectedSavingsAccountID,
  editModeEnabled,
  showUnsavedLeavePopup,
} from '../../redux/actions';
import {
  stripIllegalCharacters,
  amountWithinLimit,
  getAccountDetails,
} from '../../businessLogic/AccountsBL';
import { getSweepRequestPacket } from '../../utils/RequestPacketBuilder';
import { ToggleOption } from 'web-ui-components/lib/molecules/toggle';
import { Icon } from 'web-ui-components/lib/atoms/iconography';
import {
  getSavingAccounts,
  checkSweepErrorStatus,
  getSweepsAccountData,
  validateTargetAmount,
  updateThresholdAmount,
  validateThresholdAmount,
  setSavingAccountID,
} from '../../businessLogic/SweepsBL';
import {
  isSweepEnable,
  isLoading,
  getSavingsAccountsID,
} from '../../infrastructure/config/selectors';
import SweepsComponent from '../../components/SweepsComponent';
import { CommonModal } from '../../components/common/CommonModal';
import { fixAmount, checkValidation } from '../../utils/NumberUtils';
import { FIX_DECIMAL_BY_TWO } from '../../constants/StringConstants';

export class SweepAlertLandingPage extends Component {
  constructor(props) {
    super(props);
    this.handleTargetAmountChange = this.handleTargetAmountChange.bind(this);
    this.handleThresholdAmountChange = this.handleThresholdAmountChange.bind(this);
    this.confirmSweepChange = this.confirmSweepChange.bind(this);
    this.cancelSweepChange = this.cancelSweepChange.bind(this);
    this.handleSweepCheck = this.handleSweepCheck.bind(this);
    this.toggleOptionFactory = this.toggleOptionFactory.bind(this);
    this.handleSweepRequestPacket = this.handleSweepRequestPacket.bind(this);
    this.handleSweepAccountCardClick = this.handleSweepAccountCardClick.bind(this);
    this.errorModal = this.errorModal.bind(this);
    this.handleSelectSweepAccount = this.handleSelectSweepAccount.bind(this);
    this.retryError = this.retryError.bind(this);
    this.backClick = this.backClick.bind(this);
    const { sweeps } = props;
    this.state = {
      targetAmount: sweeps[0].target.value.toFixed(FIX_DECIMAL_BY_TWO),
      thresholdAmount: sweeps[0].threshold.value.toFixed(FIX_DECIMAL_BY_TWO),
      isButtonDisabled: false,
      showSweepAccountSelector: false,
    };
  }
  componentDidUpdate(prevProps) {
    const { masterCardLeavePopup, sweeps, isEditModeEnabled } = this.props;
    if (!isEqual(prevProps.sweeps, sweeps)) {
      this.setState({
        targetAmount: sweeps[0].target.value.toFixed(FIX_DECIMAL_BY_TWO),
        thresholdAmount: sweeps[0].threshold.value.toFixed(FIX_DECIMAL_BY_TWO),
      });
    }
    if (prevProps.isEditModeEnabled !== isEditModeEnabled && masterCardLeavePopup) {
      masterCardLeavePopup(isEditModeEnabled);
    }
  }
  componentWillUnmount() {
    const { masterCardLeavePopup, showThresholdError, showTargetError, editModeEnabled } =
      this.props;
    if (masterCardLeavePopup) {
      masterCardLeavePopup(false);
    }
    editModeEnabled(false);
    showThresholdError(false);
    showTargetError(false);
  }
  componentDidMount() {
    const { savingAccountsAvailable, setSelectedSavingsAccountID } = this.props;
    if (savingAccountsAvailable.length === 1) {
      setSelectedSavingsAccountID(head(savingAccountsAvailable).id);
    }
  }
  handleSweepRequestPacket(sweepRequestPacket, sweepId) {
    const { updateSweepAlerts, accountDetails, getSweepsList, getSweepsAlerts } = this.props;
    if (sweepId) {
      updateSweepAlerts(sweepRequestPacket, accountDetails.id, sweepId).then(() => {
        getSweepsList(accountDetails.id);
      });
    } else {
      getSweepsAlerts(sweepRequestPacket, accountDetails.id).then(() => {
        getSweepsList(accountDetails.id);
      });
    }
  }
  handleSweepCheck() {
    const { targetAmount, thresholdAmount } = this.state;
    const { savingAccountsAvailable, sweeps, accountDetails, deleteSweepAlerts, getSweepsList } =
      this.props;
    if (sweeps[0].enabled === false) {
      const sweepRequestPacket = getSweepRequestPacket(
        targetAmount,
        thresholdAmount,
        !sweeps[0].enabled,
        savingAccountsAvailable[0].id
      );
      this.handleSweepRequestPacket(sweepRequestPacket, sweeps[0].id);
    } else {
      deleteSweepAlerts(accountDetails.id, sweeps[0].id).then(() => {
        getSweepsList(accountDetails.id);
      });
    }
  }
  handleTargetAmountChange(event) {
    const { editModeEnabled } = this.props;
    const amount = stripIllegalCharacters(event.target.value);
    if (amount) {
      editModeEnabled(true);
    }
    this.setState({ isButtonDisabled: !isEmpty(amount) });
    if (amountWithinLimit(amount)) {
      this.props.showTargetError(validateTargetAmount(this.state.thresholdAmount, amount));
      this.setState({ targetAmount: amount });
    }
  }
  handleThresholdAmountChange(event) {
    const { showThresholdError, editModeEnabled } = this.props;
    const amount = stripIllegalCharacters(event.target.value);
    if (amount) {
      editModeEnabled(true);
    }
    this.setState({ isButtonDisabled: !isEmpty(amount) });
    showThresholdError(checkValidation(amount));
    if (amountWithinLimit(amount)) {
      if (updateThresholdAmount(this.state.targetAmount, amount)) {
        this.setState({ targetAmount: updateThresholdAmount(this.state.targetAmount, amount) });
      }
      if (!amount.length || validateThresholdAmount(amount)) {
        this.setState({ thresholdAmount: amount });
      }
    }
  }
  confirmSweepChange() {
    const { targetAmount, thresholdAmount } = this.state;
    const { savingsAccountID, sweeps, isLoading, editModeEnabled } = this.props;
    const sweepRequestPacket = getSweepRequestPacket(
      targetAmount,
      thresholdAmount,
      true,
      savingsAccountID
    );
    this.handleSweepRequestPacket(sweepRequestPacket, sweeps[0].id);
    this.setState({ isButtonDisabled: isLoading });
    editModeEnabled(false);
  }
  cancelSweepChange() {
    const { sweeps, setSelectedSavingsAccountID, editModeEnabled } = this.props;
    setSelectedSavingsAccountID(sweeps[0].other_account_id);
    this.setState({
      targetAmount: sweeps[0].target.value.toFixed(FIX_DECIMAL_BY_TWO),
      thresholdAmount: sweeps[0].threshold.value.toFixed(FIX_DECIMAL_BY_TWO),
      isButtonDisabled: false,
    });
    editModeEnabled(false);
  }
  toggleOptionFactory() {
    const { content, sweeps } = this.props;
    const displayIcon = (
      <Icon iconType="GenericMoneyOutlineLarge" className="c-a-button__icon" direction="right" />
    );
    return (
      <ToggleOption
        icon={displayIcon}
        title={content.sweepTitle}
        active={sweeps[0].enabled}
        onChange={this.handleSweepCheck}
      />
    );
  }
  retryError(type, packet) {
    const {
      accountDetails,
      getSweepsAlerts,
      getSweepsList,
      sweeps,
      updateSweepAlerts,
      deleteSweepAlerts,
    } = this.props;
    switch (type) {
      case 'POST':
        getSweepsAlerts(packet, accountDetails.id).then(() => {
          getSweepsList(accountDetails.id);
        });
        break;
      case 'PUT':
        updateSweepAlerts(packet, accountDetails.id, sweeps[0].id).then(() => {
          getSweepsList(accountDetails.id);
        });
        break;
      case 'DELETE':
        deleteSweepAlerts(accountDetails.id, sweeps[0].id).then(() => {
          getSweepsList(accountDetails.id);
        });
        break;
    }
  }
  errorModal() {
    const { updateSweepError, closeErrorPopup, retryType, sweepPacket } = this.props;
    return (
      <CommonModal
        isOpen={updateSweepError.isError}
        onRequestClose={() => {
          closeErrorPopup(false);
        }}
        onPrimaryBtnClick={() => this.retryError(retryType, sweepPacket)}
      />
    );
  }
  handleSweepAccountCardClick() {
    const { savingAccountsAvailable } = this.props;
    if (savingAccountsAvailable.length !== 1) {
      this.setState({
        showSweepAccountSelector: !this.state.showSweepAccountSelector,
      });
    }
  }
  handleSelectSweepAccount(selectedSavingAccountID) {
    const { savingsAccountID, setSelectedSavingsAccountID } = this.props;
    if (!isEqual(savingsAccountID, selectedSavingAccountID)) {
      this.setState({ isButtonDisabled: true });
    }
    setSelectedSavingsAccountID(selectedSavingAccountID);
  }
  backClick() {
    const { isEditModeEnabled, routeTo, showUnsavedLeavePopup } = this.props;
    if (isEditModeEnabled) {
      showUnsavedLeavePopup(true);
    } else {
      routeTo('/singlealerts');
    }
  }
  render() {
    const { targetAmount, thresholdAmount, isButtonDisabled, showSweepAccountSelector } =
      this.state;
    const {
      accountDetails,
      savingAccountsAvailable,
      sweeps,
      content,
      isLoading,
      routeTo,
      showThresholdMsg,
      showTargetMsg,
      updateSweepError,
      savingsAccountID,
      isLeavePopupEnable,
      showUnsavedLeavePopup,
    } = this.props;
    return (
      <div>
        <SweepsComponent
          backClick={this.backClick}
          accountDetails={accountDetails}
          content={content}
          isEnabled={sweeps[0].enabled}
          toggleOptionFactory={this.toggleOptionFactory}
          handleTargetAmountChange={this.handleTargetAmountChange}
          targetAmount={targetAmount}
          savingAccountsAvailable={savingAccountsAvailable}
          thresholdAmount={thresholdAmount}
          handleSweepCheck={this.handleSweepCheck}
          handleThresholdAmountChange={this.handleThresholdAmountChange}
          isButtonDisabled={isButtonDisabled}
          cancelSweepChange={this.cancelSweepChange}
          confirmSweepChange={this.confirmSweepChange}
          isLoading={isLoading}
          showThresholdMsg={showThresholdMsg}
          showTargetMsg={showTargetMsg}
          handleSweepAccountCardClick={this.handleSweepAccountCardClick}
          showSweepAccountSelector={showSweepAccountSelector}
          handleSelectSweepAccount={this.handleSelectSweepAccount}
          savingsAccountID={setSavingAccountID(savingsAccountID, sweeps)}
          errorModal={this.errorModal}
          sweepError={updateSweepError}
          handleTargetOnBlur={(event) => {
            this.setState({ targetAmount: fixAmount(event.target.value) });
          }}
          handleThresholdOnBlur={(event) => {
            this.setState({ thresholdAmount: fixAmount(event.target.value) });
          }}
        />
        {isLeavePopupEnable && (
          <CommonModal
            isOpen={isLeavePopupEnable}
            onRequestClose={() => {
              showUnsavedLeavePopup(false);
              routeTo('/singlealerts');
            }}
            onPrimaryBtnClick={() => {
              showUnsavedLeavePopup(false);
            }}
            header={content.leavePopupHeader}
            infoCardContent={content.leavePopupContent}
            primaryBtnName={content.leavePopupPrimaryBtnName}
            secondaryBtnName={content.leavePopupSecondaryBtnName}
          />
        )}
      </div>
    );
  }
}
SweepAlertLandingPage.propTypes = {
  isEnabled: PropTypes.bool,
  savingAccountsAvailable: PropTypes.array,
  sweeps: PropTypes.array,
  backClick: PropTypes.func,
  content: PropTypes.shape({
    sweepsInfoText: PropTypes.string,
    sweepTitle: PropTypes.string,
    sweepInfoText: PropTypes.string,
    leavePopupHeader: PropTypes.string,
    leavePopupContent: PropTypes.string,
    leavePopupPrimaryBtnName: PropTypes.string,
    leavePopupSecondaryBtnName: PropTypes.string,
  }),
  accountDetails: PropTypes.shape({
    id: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
  updateSweepAlerts: PropTypes.func,
  getSweepsList: PropTypes.func,
  routeTo: PropTypes.func,
  getSweepsAlerts: PropTypes.func,
  showThresholdMsg: PropTypes.bool,
  showTargetMsg: PropTypes.bool,
  showThresholdError: PropTypes.func,
  showTargetError: PropTypes.func,
  updateSweepError: PropTypes.bool,
  closeErrorPopup: PropTypes.func.isRequired,
  deleteSweepAlerts: PropTypes.func,
  setSelectedSavingsAccountID: PropTypes.func,
  savingsAccountID: PropTypes.string,
  retryType: PropTypes.string,
  sweepPacket: PropTypes.shape({}),
  masterCardLeavePopup: PropTypes.func,
  editModeEnabled: PropTypes.func,
  isEditModeEnabled: PropTypes.bool,
  isLeavePopupEnable: PropTypes.bool,
  showUnsavedLeavePopup: PropTypes.func,
};

const mapStateToProps = ({ accountList, sweepList, loader, validation }) => {
  const sweepError = checkSweepErrorStatus(sweepList) || false;
  return {
    sweeps: getSweepsAccountData(sweepError, sweepList),
    accountDetails: getAccountDetails(
      accountList.get('selectedAccountId'),
      accountList.get('accountList').accounts
    ),
    savingAccountsAvailable: getSavingAccounts(accountList.get('accountList').accounts),
    updateSweepError: sweepList.get('isError'),
    isSweepEnable: isSweepEnable(accountList),
    isLoading: isLoading(loader),
    showThresholdMsg: validation.get('thresholdValidation'),
    showTargetMsg: validation.get('targetValidation'),
    savingsAccountID: getSavingsAccountsID(accountList),
    sweepError: sweepList.get('sweepError'),
    retryType: sweepList.get('category'),
    sweepPacket: sweepList.get('sweepPacket'),
    isEditModeEnabled: validation.get('enableEditMode'),
    isLeavePopupEnable: validation.get('enableUnsavedLeavePopup'),
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateSweepAlerts,
      getSweepsList,
      getSweepsAlerts,
      showThresholdError,
      showTargetError,
      setSelectedSavingsAccountID,
      closeErrorPopup,
      deleteSweepAlerts,
      editModeEnabled,
      showUnsavedLeavePopup,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(SweepAlertLandingPage);
